import './style';
import { render, Component } from 'preact';
import { version } from '../package.json';

import { Router, route } from 'preact-router';
import Match from 'preact-router/match';
import Helmet from 'preact-helmet';

import find from 'dlv';
import {
  setAffiliate,
  setPreferences,
  getCookies,
  deleteCookie,
  getParams,
  setInternalTrackingTag,
} from '@helpers';
import analytics, { facebookTracking } from '@analytics';
import { PRODUCTION } from '@globals';

import { SlotProvider } from '@components/slots';
import Header from '@components/header';
import { HeroWrapper } from '@components/hero';
import Footer from '@components/footer';
import { PubSub } from '@components/pubsub';
import root from '@components/root';

import AuthProvider from '@utils/auth';
import I18N from '@utils/i18n';
import I18n from '@i18n';

// Code-splitting is automated for routes
import Landing from 'async!@routes/landing';
import Search from 'async!@routes/search';
import Confirmation from 'async!@routes/confirmation';
import Profile from 'async!@routes/profile';
import DynamicRoute from 'async!@routes/dynamic';

import Admin from 'async!@routes/admin';
import Changes from 'async!@routes/admin/changes';
import Ads from 'async!@components/ads';
import TopBar from '@components/topbar';
import HybridPage from 'async!@components/hybrid-page';
let clicks = 0;

// Static list of hybrid routes
const HYBRID_ROUTES = {
  '/fi/liity-tableonlineen': true,
  '/en/join': true,
  // Add more hybrid routes as needed
};

export default class App extends Component {
  kkeys = [];
  constructor(props) {
    super(props);

    // Sets router reference to the window for the route function to work outside of the component
    window.route = route;
    // eslint-disable-next-line no-console
    console.log(`%cTableOnline version ${version}`, `color:#005c53`);

    const lang = root.location.pathname.split('/').filter(b => b)[0];
    if (lang && I18n.supportedLocales.includes(lang)) {
      I18n.changeLocale(lang);
    } else {
      if (lang) {
        route(`/${this.state.defaultLanguage}${window.location.pathname}`);
      }
    }

    // Test hidden affiliates
    const cookies = getCookies();
    const queryPreferences = getParams();

    setPreferences(queryPreferences);

    if (cookies['TT2']) {
      deleteCookie('TableOnline--affiliate', '/');
    }

    if (typeof navigator !== 'undefined' || typeof document !== 'undefined') {
      this.state = {
        latest: version,
        accessGranted:
          document.querySelector('main').innerHTML ||
          PRODUCTION ||
          cookies.password === '*zM?Y%AvvK7+-jNw5^Tq5g8^c$mn#F$J',
        defaultLanguage:
          (cookies.lang && I18n.supportedLocales.includes(cookies.lang)
            ? cookies.lang
            : [
                ...find(navigator, 'languages'),
                find(navigator, 'language'),
              ].filter(language =>
                I18n.supportedLocales.includes(language)
              )[0]) || 'fi',
        breadcrumbs: [],
      };
    } else {
      this.state = {
        latest: version,
        accessGranted: true,
        defaultLanguage: 'fi',
        breadcrumbs: [],
      };
    }

    // Start trackers
    analytics({ ecommerce: null });
    facebookTracking();
  }
  /** Gets fired when the route changes.
   *	@param {Object} event		"change" event from [preact-router](http://git.io/preact-router)
   *	@param {string} event.url	The newly routed URL
   */
  handleRoute = e => {
    if (e.url === this.currentUrl) {
      return;
    }
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (find(e, 'current.attributes')) {
      this.handleAffiliateLinking(find(e, 'current.attributes'));
      this.handleInternalTagging(find(e, 'current.attributes'));
    }
    if (this.currentUrl) {
      facebookTracking();
    }

    this.currentUrl = e.url;

    let newLocale =
      root.location.pathname.split('/').filter(b => b)[0] ||
      this.state.defaultLanguage;

    if (I18n.supportedLocales.includes(newLocale)) {
      I18n.changeLocale(newLocale);
      if (typeof document !== 'undefined')
        document.cookie = `lang=${newLocale}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/`;
      render(
        <Helmet
          htmlAttributes={{ lang: newLocale === 'ee' ? 'et' : newLocale }}
        />,
        document.body
      );
    }
  };
  componentDidMount() {
    if (this.state.accessGranted === false) {
      root.addEventListener('click', this.konami);
      root.addEventListener('touchend', this.konami);
    }
  }

  // Check if current path is a hybrid route
  isHybridRoute = path => {
    return HYBRID_ROUTES[path] || false;
  };

  konami = () => {
    clicks++;
    if (clicks >= 4) {
      this.setState({ accessGranted: true }, () => {
        if (typeof document !== 'undefined')
          document.cookie =
            'password=*zM?Y%AvvK7+-jNw5^Tq5g8^c$mn#F$J; expires=Fri, 31 Dec 9999 23:59:59 GMT';
      });
      root.removeEventListener('click', this.konami);
      root.removeEventListener('touchend', this.konami);
    }
  };
  handleAffiliateLinking = params => {
    if (params.tlink) {
      setAffiliate('tlink', params.tlink);
    } else if (params.rewardno) {
      setAffiliate('norwegian', params.rewardno);
    } else if (params.finnair) {
      setAffiliate('finnair', params.finnair);
    }
    return;
  };
  handleInternalTagging = params => {
    if (params.tags) {
      setInternalTrackingTag('tags', (params.tags || '').split(','));
    }
    return;
  };

  getChildContext() {
    return {
      supportedLocales: ['fi', 'en', 'ee'], // I18n.supportedLocales,
      setBreadcrumbs: breadcrumbs => {
        if (
          !breadcrumbs ||
          !this.state.breadcrumbs ||
          JSON.stringify(breadcrumbs) !== JSON.stringify(this.state.breadcrumbs)
        ) {
          this.setState({ breadcrumbs });
        }
      },
    };
  }
  shouldComponentUpdate(nextProps, nextState) {
    return JSON.stringify(nextState) !== JSON.stringify(this.state);
  }
  render(
    props,
    { accessGranted = false, breadcrumbs, defaultLanguage, latest }
  ) {
    return accessGranted && defaultLanguage && latest ? (
      <I18N>
        <PubSub>
          <AuthProvider>
            <SlotProvider>
              {this.isHybridRoute(root.location.pathname) ? (
                <HybridPage
                  path={root.location.pathname}
                  lang={defaultLanguage}
                />
              ) : (
                <main id="app">
                  <Helmet
                    htmlAttributes={{
                      lang: defaultLanguage === 'ee' ? 'et' : defaultLanguage,
                    }}
                    titleTemplate="%s | TableOnline.fi"
                    titleAttributes={{
                      itemprop: 'name',
                      lang: defaultLanguage === 'ee' ? 'et' : defaultLanguage,
                    }}
                    meta={[
                      {
                        name: 'description',
                        content: I18n.t(
                          'Make reservations, read reviews, find offers and discover the top restaurants in Finland and Tallinn. Search hundreds of restaurants and find the best!'
                        ),
                      },
                      { property: 'og:type', content: 'article' },
                    ]}
                    onChangeClientState={() => [
                      ...document.head
                        .querySelectorAll('[data-preact-helmet="true"]')
                        .forEach(el =>
                          document.head
                            .querySelector('title')
                            .insertAdjacentElement('afterEnd', el)
                        ),
                    ]}
                  />
                  <TopBar />
                  <HeroWrapper breadcrumbs={breadcrumbs} />
                  <Router onChange={this.handleRoute}>
                    <Match path="/">
                      {() => {
                        route(`/${defaultLanguage}`, true);
                      }}
                    </Match>
                    <Landing path="/:lang/" />
                    <Search path="/:lang/search" />
                    <Admin path="/:lang/admin" />
                    <Changes path="/:lang/admin/changes" />
                    <Profile path="/:lang/profile/:view?" />
                    <Confirmation path="/:lang/booking/:bookingId/:hash" />
                    <DynamicRoute
                      path="/:lang/:city/:slug?/:restaurantId?/:action?"
                      default
                    />
                  </Router>
                  <span class="verticalSpacer" />
                  <Match path="/:lang/:city/:slug/:restaurantId/book">
                    {e => {
                      const split = e.path.split('/');
                      return (split.length === 6 &&
                        split.slice(-1)[0] === 'book') ||
                        split.slice(-1)[0] === 'giftcard' ? null : (
                        <Ads />
                      );
                    }}
                  </Match>

                  <Footer />
                </main>
              )}
            </SlotProvider>
          </AuthProvider>
        </PubSub>
      </I18N>
    ) : (
      false
    );
  }
}



// WEBPACK FOOTER //
// ./index.js